import type { ApiError } from '@/services/api/client';

/** Matches backend `MESSAGE_CONSTANTS.HTTP_ERROR_CODES` and Angular `HTTP_ERRORS.CUSTOM_ERRORS`. */
export const LOGIN_API_ERROR_CODES = {
  FORBIDDEN_UNDER_REVIEW: 'E403_001',
  FORBIDDEN_PERMISSION_DENIED: 'E403_002',
  LOCKED_LOCATION_REQUIRED: 'E423_001',
} as const;

/**
 * User-facing login strings aligned with Angular `LOGIN_ERROR_RESPONSE` in
 * `frontend/src/app/common/constants.ts` (MESSAGE fields).
 */
export const LOGIN_API_USER_MESSAGES = {
  UNDER_REVIEW: 'Your account approval is pending',
  PERMISSION_DENIED:
    'We understand your request, but your current permissions don’t allow access to this page.',
  SUSPENDED: 'Your account approval is pending',
  LOCATION_REQUIRED:
    'Your role requires a location to be assigned. No location was found for your account. Please contact your administrator.',
  INTERNAL_SERVER_ERROR:
    'We’re currently experiencing a system issue and can’t process this action at the moment. Please contact Support for further assistance.',
} as const;

const GENERIC_AUTH_CONTACT_ADMIN =
  'Authentication failed. Please contact your administrator.';

function getDescriptionCode(data: ApiError['data']): string {
  if (data == null || typeof data !== 'object' || !('description' in data)) {
    return '';
  }
  const d = (data as { description: unknown }).description;
  return typeof d === 'string' ? d.trim() : '';
}

function getMessageFromBody(data: ApiError['data']): string {
  if (data?.message) {
    if (Array.isArray(data.message)) {
      return String(data.message[0] ?? '').trim();
    }
    if (typeof data.message === 'string') {
      return data.message.trim();
    }
  }
  return '';
}

function isKnownMachineCode(description: string): boolean {
  return (Object.values(LOGIN_API_ERROR_CODES) as string[]).includes(description);
}

/**
 * Maps `POST /auth/login` errors the same way as Angular `AuthUserService.errorHandler`:
 * status + `description` codes first, then `message`, then fallbacks.
 */
export function resolveLoginErrorFromApiError(error: ApiError): string {
  const code = getDescriptionCode(error.data);
  const status = error.status;

  if (status === 403 && code === LOGIN_API_ERROR_CODES.FORBIDDEN_UNDER_REVIEW) {
    return LOGIN_API_USER_MESSAGES.UNDER_REVIEW;
  }
  if (status === 403 && code === LOGIN_API_ERROR_CODES.FORBIDDEN_PERMISSION_DENIED) {
    return LOGIN_API_USER_MESSAGES.PERMISSION_DENIED;
  }
  if (status === 423 && code === LOGIN_API_ERROR_CODES.LOCKED_LOCATION_REQUIRED) {
    return LOGIN_API_USER_MESSAGES.LOCATION_REQUIRED;
  }
  if (status === 423) {
    return LOGIN_API_USER_MESSAGES.SUSPENDED;
  }

  const fromMessage = getMessageFromBody(error.data);
  const fromDescriptionRaw = (() => {
    const d = getDescriptionCode(error.data);
    if (!d || isKnownMachineCode(d)) {
      return '';
    }
    return d;
  })();

  if (fromMessage) {
    return fromMessage;
  }
  if (fromDescriptionRaw) {
    return fromDescriptionRaw;
  }

  const fallbackDisplay = error.getDisplayMessage();
  if (fallbackDisplay && !/^HTTP \d+/.test(fallbackDisplay)) {
    return fallbackDisplay;
  }

  if (status === 401 || status === 403) {
    return GENERIC_AUTH_CONTACT_ADMIN;
  }

  if (status >= 500) {
    return LOGIN_API_USER_MESSAGES.INTERNAL_SERVER_ERROR;
  }

  return fallbackDisplay || 'Failed to sign in. Please try again.';
}
